import {
  AnimatedSprite,
  Container,
  Graphics,
  InteractionEvent,
  LINE_CAP,
  Loader,
  Sprite,
  Text,
  Texture,
  VideoResource,
} from 'pixi.js';
import { Player } from '../class/final';
import Cucumber from '../class/final/Cucumber';
import { Canvas } from '../class/final';
import { Game, Scene, Store, TextureFactory } from '../core';
import { DrawingPatternData, IEventEmitter, Updatable, Vector } from '../interface';
import { DrawingGameUI, VanishText } from '../ui';
import MainScene from './MainScene';
import ClosingScene from './ClosingScene';
import PlayerProjectile from '../class/final/Projectile';
import { secondsFormatting, secondsToScore } from '../util';
import { sound } from '@pixi/sound';
import Lighting from '../class/final/Lighting';
import { Emitter } from '@pixi/particle-emitter';
import ParticleGhost from '../constant/ParticleGhost';
import ThunderInfo from '../class/final/thunder.json';
import MeteorInfo from '../class/final/meteor.json';
import { LOCALE_DATA } from '../constant';
import { logging, LOG_EVENT, LOG_SCENE } from '../api/log';
import { loadGoogleAdvertisement, showGoogleAdvertisement } from '../util/AD';

class TutorialUI {
  public container: Container;
  private graphics: Graphics;
  private mouse: Sprite;
  private dt: number = 0;
  private maxDt: number = 120;
  private tutorialText: Text;

  constructor() {
    this.container = new Container();
    this.graphics = new Graphics();
    this.mouse = new Sprite(TextureFactory.instance.getSingleTexture('/textures/ui/mouse.png'));
    this.mouse.anchor.set(0.1, 0.1);
    this.mouse.scale.set(0.75);

    this.tutorialText = new Text(LOCALE_DATA[Store.instance.state.locale].FINAL_TUTORIAL_1, {
      fontSize: 65,
      fontWeight: '700',
      fontFamily: 'gaegu',
      fontStyle: 'normal',
      align: 'center',
      stroke: 0xffffff,
      strokeThickness: 6,
      fill: '#4a282f',
    });

    this.tutorialText.anchor.set(0.5);
    this.tutorialText.x = 540;
    this.tutorialText.y = 700;

    this.container.addChild(this.graphics);
    this.container.addChild(this.tutorialText);
    this.container.addChild(this.mouse);
  }

  public update(pattern?: number): void {
    this.dt += 1;
    if (this.dt >= this.maxDt) {
      this.dt = 0;
    }

    this.graphics.clear();
    this.graphics.lineStyle({
      width: 20,
      color: 0xffffff,
      cap: LINE_CAP.ROUND,
    });
    switch (pattern) {
      case 0: {
        this.tutorialText.text = LOCALE_DATA[Store.instance.state.locale].FINAL_TUTORIAL_1;
        const basePosition = {
          x: 540,
          y: 960,
        };
        this.graphics.moveTo(basePosition.x - 100, basePosition.y);
        this.graphics.lineTo(basePosition.x - 100 + 200 * (this.dt / this.maxDt), basePosition.y);
        this.mouse.x = basePosition.x - 100 + 200 * (this.dt / this.maxDt) - 15;
        this.mouse.y = basePosition.y;
        break;
      }
      case 1: {
        this.tutorialText.text = LOCALE_DATA[Store.instance.state.locale].FINAL_TUTORIAL_2;
        const basePosition = {
          x: 540,
          y: 960,
        };

        this.graphics.moveTo(basePosition.x, basePosition.y);
        this.graphics.lineTo(basePosition.x, basePosition.y + 200 * (this.dt / this.maxDt));
        this.mouse.x = basePosition.x - 15;
        this.mouse.y = basePosition.y + 200 * (this.dt / this.maxDt);
        break;
      }
      case 2: {
        this.tutorialText.text = LOCALE_DATA[Store.instance.state.locale].FINAL_TUTORIAL_3;
        const basePosition = {
          x: 540,
          y: 1160,
        };

        this.graphics.moveTo(basePosition.x, basePosition.y);
        this.graphics.lineTo(basePosition.x, basePosition.y - 200 * (this.dt / this.maxDt));
        this.mouse.x = basePosition.x - 15;
        this.mouse.y = basePosition.y - 200 * (this.dt / this.maxDt);
        break;
      }
      default:
        break;
    }
  }
}

class CucumberProjectile extends Container {
  public velocity: Vector;
  public type: number;

  constructor(type: number) {
    super();
    this.velocity = { x: 0, y: 0 };
    this.type = type;

    const texture = TextureFactory.instance.getSingleTexture('/textures/objects/etc/projectile.png');
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.scale.set(0.8);
    this.addChild(sprite);
  }

  public update(elapsed: number): void {
    this.position.x += this.velocity.x * elapsed;
    this.position.y += this.velocity.y * elapsed;
  }
}

class Thunder extends Container {
  public animatedSprite: AnimatedSprite;
  public complete: boolean = false;

  constructor() {
    super();

    const textures = TextureFactory.instance.load(ThunderInfo);
    this.animatedSprite = new AnimatedSprite(textures);
    this.animatedSprite.loop = false;
    this.animatedSprite.animationSpeed = 0.4;
    this.animatedSprite.updateAnchor = true;
    this.animatedSprite.scale.set(2);
    this.animatedSprite.play();

    this.animatedSprite.onComplete = () => {
      this.complete = true;
      this.emit('complete');
    };

    this.addChild(this.animatedSprite);
  }
}
class Meteor extends Container {
  public animatedSprite: AnimatedSprite;
  public complete: boolean = false;

  constructor() {
    super();

    const textures = TextureFactory.instance.load(MeteorInfo);
    this.animatedSprite = new AnimatedSprite(textures);
    this.animatedSprite.loop = false;
    this.animatedSprite.animationSpeed = 0.2 + Math.random() * 0.2;
    this.animatedSprite.updateAnchor = true;
    this.animatedSprite.scale.set(1.5 + Math.random());
    this.animatedSprite.play();

    this.animatedSprite.onComplete = () => {
      this.complete = true;
      this.emit('complete');
    };

    this.addChild(this.animatedSprite);
  }
}
class BossHelathBar extends Container {
  private bar: AnimatedSprite;
  private thumbnail: AnimatedSprite;
  private maskingGraphics: Graphics;

  constructor() {
    super();

    const baseTexture = TextureFactory.instance.getSingleTexture('/textures/ui/health_bar.png');
    const baseSprite = new Sprite(baseTexture);
    baseSprite.anchor.set(0.5);
    this.addChild(baseSprite);

    const barTextures = [
      TextureFactory.instance.getSingleTexture('/textures/ui/health_0.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/health_1.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/health_2.png'),
    ];
    this.bar = new AnimatedSprite(barTextures);
    this.bar.anchor.set(0.5);
    this.bar.y += 12;
    this.addChild(this.bar);

    this.maskingGraphics = new Graphics();
    this.maskingGraphics.drawRect(-this.bar.width / 2, -this.bar.height / 2 + 13, this.bar.width, this.bar.height);
    this.addChild(this.maskingGraphics);
    this.bar.mask = this.maskingGraphics;

    const thumbnailTextures = [
      TextureFactory.instance.getSingleTexture('/textures/ui/oee_0.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/oee_1.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/oee_2.png'),
      TextureFactory.instance.getSingleTexture('/textures/ui/oee_3.png'),
    ];
    this.thumbnail = new AnimatedSprite(thumbnailTextures);
    this.thumbnail.anchor.set(0.5, 0.4);
    this.thumbnail.x += 300;
    this.addChild(this.thumbnail);
  }

  public update(health: number, maxHealth: number): void {
    this.maskingGraphics.clear();
    this.maskingGraphics.drawRect(
      -this.bar.width / 2,
      -this.bar.height / 2 + 13,
      (this.bar.width * health) / maxHealth,
      this.bar.height
    );
    this.thumbnail.x = -300 + (600 * health) / maxHealth;

    if (health === 0) {
      this.bar.gotoAndStop(2);
      this.thumbnail.gotoAndStop(3);
    } else if (health / maxHealth < 0.35) {
      this.bar.gotoAndStop(2);
      this.thumbnail.gotoAndStop(2);
    } else if (health / maxHealth < 0.7) {
      this.bar.gotoAndStop(1);
      this.thumbnail.gotoAndStop(1);
    } else {
      this.bar.gotoAndStop(0);
      this.thumbnail.gotoAndStop(0);
    }
  }
}

class FinalGameScene extends Scene {
  private cucumber: Cucumber;
  private player: Player;
  private canvas: Canvas;
  private ui: DrawingGameUI;
  private bossHealth: BossHelathBar;
  private objects: Array<Updatable & IEventEmitter>;
  private cucumberProjectiles: Array<CucumberProjectile>;
  private playerProjectiles: Array<PlayerProjectile>;
  private isBgmPlaying: boolean = false;
  private tutorialUI: TutorialUI;
  private tutorial = {
    attack: false,
    jump: false,
    slide: false,
  };
  private lighting?: Lighting;
  private finalAttackBG: Sprite;
  private particleEmitter: Emitter;

  constructor(game: Game) {
    super(game);

    this.objects = [];
    this.cucumberProjectiles = [];
    this.playerProjectiles = [];

    const bg = new Sprite(TextureFactory.instance.getSingleTexture('/textures/bg/bg_final.png'));
    bg.x = -1;
    this.stage.addChild(bg);

    this.finalAttackBG = new Sprite(Texture.EMPTY);

    if ([0, 2, 3, 6].includes(Store.instance.state.costume)) {
      this.finalAttackBG.texture = TextureFactory.instance.getSingleTexture('/textures/bg/bg_final_attack.png');
    } else {
      const video: HTMLVideoElement = document.createElement('video');

      video.src = `/textures/bg/bg_final_attack_${Store.instance.state.costume}.mp4`;
      video.muted = true;
      video.playsInline = true;
      video.autoplay = false;
      video.loop = true;
      video.preload = 'auto';

      video.pause();

      this.finalAttackBG.texture = Texture.from(video);
      (this.finalAttackBG.texture.baseTexture.resource as any).autoPlay = false;
      (this.finalAttackBG.texture.baseTexture.resource as VideoResource).source.pause();
    }
    this.finalAttackBG.visible = false;
    this.stage.addChild(this.finalAttackBG);

    this.camera.positionChasingCoefficient = 0.015;
    this.camera.setOffset({ x: 0, y: 0 });
    this.camera.setTargetPosition({ x: 540, y: 960 }, true);
    this.camera.setBoundary({
      min: { x: 0, y: 0 },
      max: { x: 1080, y: 1920 },
    });

    this.cucumber = new Cucumber();
    this.cucumber.position.x = 720;
    this.cucumber.position.y = 1310;
    this.cucumber.on('damaged', (damage: number) => {
      const scoreText: VanishText = new VanishText(`HP -${damage}`, { fill: 0xff5555 });
      scoreText.x = this.cucumber.position.x;
      scoreText.y = this.cucumber.position.y - 270;
      scoreText.on('destroy', () => {
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
    });
    this.cucumber.on('DIE', (): void => {
      sound.play('/audio/robot_destroy.mp3', { loop: false, volume: Store.instance.state.volume as number });
      this.gameClear();
    });
    this.cucumber.on('PROJECTILE', (type: number) => {
      sound.play('/audio/boss_attack.mp3', { loop: false, volume: Store.instance.state.volume as number });
      const projectile = new CucumberProjectile(type);
      projectile.velocity.x = -20;

      if (type === 1) {
        projectile.position.x = this.cucumber.position.x - 160;
        projectile.position.y = this.cucumber.position.y - 85;
      } else if (type === 2) {
        projectile.velocity.y = 3.5;
        projectile.position.x = this.cucumber.position.x - 160;
        projectile.position.y = this.cucumber.position.y - 200;
      } else {
        projectile.velocity.y = 2;
        projectile.position.x = this.cucumber.position.x + 90;
        projectile.position.y = this.cucumber.position.y - 200;
      }
      this.stage.addChild(projectile);
      this.cucumberProjectiles.push(projectile);
    });
    this.stage.addChild(this.cucumber.container);

    this.player = new Player();
    this.player.position.x = 260;
    this.player.position.y = 1310;
    this.player.on('DIE', (): void => {
      this.gameOver();
    });
    this.player.on('damaged', (damage: number) => {
      const scoreText: VanishText = new VanishText(`HP -${damage}`, { fill: 0xff5555 });
      scoreText.x = this.player.position.x;
      scoreText.y = this.player.position.y - 90;
      scoreText.on('destroy', () => {
        this.stage.removeChild(scoreText);
        scoreText.destroy();
      });
      this.objects.push(scoreText);
      this.stage.addChild(scoreText);
    });
    this.player.on('PROJECTILE', () => {
      this.tutorial.attack = true;
      const projectile = new PlayerProjectile();
      projectile.velocity.x = 20;
      projectile.velocity.y = 2 - Math.floor(Math.random() * 6);

      projectile.x = this.player.position.x + 40;
      projectile.y = this.player.position.y - 60;

      this.stage.addChild(projectile);
      this.playerProjectiles.push(projectile);
    });
    this.player.on('LAST_ATTACK_DONE', () => {
      this.cucumber.hit(999999);
      this.finalAttackBG.visible = false;
      this.ui.unFocus();
      if (![0, 2, 3, 6].includes(Store.instance.state.costume)) {
        (this.finalAttackBG.texture.baseTexture.resource as VideoResource).source.pause();
      }
      this.particleEmitter.emit = false;
    });
    this.player.on('PARTICLE', () => {
      if (Store.instance.state.costume === 3) {
        this.particleEmitter.spawnPos.set(this.player.position.x, this.player.position.y - 350);
        this.particleEmitter.emit = true;
      }
    });
    this.player.on('METEOR', () => {
      const meteorFx = new Meteor();
      if (Math.random() < 0.5) {
        meteorFx.scale.x = meteorFx.scale.x * -1;
      }
      meteorFx.x = Math.random() * 1080;
      meteorFx.y = this.player.position.y + Math.random() * 150;
      meteorFx.on('complete', () => {
        this.stage.removeChild(meteorFx);
        meteorFx.destroy();
      });
      this.stage.addChild(meteorFx);
    });
    this.player.on('THUNDER', () => {
      this.ui.lighting();

      const thunderFx = new Thunder();
      thunderFx.x = Math.random() * 1080;
      thunderFx.on('complete', () => {
        this.stage.removeChild(thunderFx);
        thunderFx.destroy();
      });
      this.stage.addChild(thunderFx);
    });
    this.player.on('LAST_ATTACK_HIT', () => {
      this.cucumber.trigger('LAST_HIT');
    });

    this.stage.addChild(this.player.container);

    if ([1, 4, 6].includes(Store.instance.state.costume)) {
      this.stage.sortableChildren = true;
      this.cucumber.container.zIndex = 1;
    }

    this.particleEmitter = new Emitter(this.stage, ParticleGhost);
    this.particleEmitter.emit = false;

    this.canvas = new Canvas();
    this.canvas.on('drawing-pattern-found', (result: { similarity: number; patternData?: DrawingPatternData }) => {
      const isJump =
        result.patternData?.pattern === 1 &&
        this.canvas.points[this.canvas.points.length - 1].y < this.canvas.points[0].y;
      const isSlide =
        result.patternData?.pattern === 1 &&
        this.canvas.points[this.canvas.points.length - 1].y > this.canvas.points[0].y;
      const isAttack = result.patternData?.pattern === 0;

      if (isJump) {
        this.player.jump();
        if (this.cucumber.attackProcess === 1) {
          this.cucumber.attackProcess = 2;
        }
      } else if (isSlide) {
        this.player.trigger('SLIDE');
        if (this.cucumber.attackProcess === 3) {
          this.cucumber.attackProcess = 4;
        }
      } else if (isAttack) {
        if (this.cucumber.health - this.playerProjectiles.length <= 10 && this.player.state !== 'LAST_ATTACK') {
          // LAST ATTACK
          this.finalAttackBG.visible = true;
          if (![0, 2, 3, 6].includes(Store.instance.state.costume)) {
            (this.finalAttackBG.texture.baseTexture.resource as VideoResource).source.play();
          }
          sound.play('/audio/nini_finalattack.mp3', { loop: false, volume: Store.instance.state.volume as number });
          this.ui.lighting();
          this.ui.focus();
          this.lighting = new Lighting();
          this.lighting.x = this.player.position.x;
          this.lighting.y = this.player.position.y;
          this.stage.addChild(this.lighting);

          const currentZoom = this.camera.zoom;
          this.camera.setTargetPosition({
            x: this.player.position.x,
            y: 960,
          });
          this.camera.setTargetZoom(1.2);

          setTimeout(() => {
            this.camera.setTargetPosition({ x: 540, y: 960 });
            this.camera.setTargetZoom(currentZoom);
          }, 2000);

          this.player.trigger('LAST_ATTACK');
          this.cucumber.attackDT = -Infinity;
          this.cucumber.maxAttackDT = Infinity;

          for (const cucumberProjectile of this.cucumberProjectiles) {
            this.stage.removeChild(cucumberProjectile);
            this.cucumberProjectiles.splice(this.cucumberProjectiles.indexOf(cucumberProjectile), 1);
          }
        } else {
          this.player.trigger('ATTACK');
        }
      }
    });
    this.container.addChild(this.canvas);

    this.bossHealth = new BossHelathBar();
    this.bossHealth.x = this.game.width / 2;
    this.bossHealth.y = this.game.height - 100;
    this.bossHealth.scale.set(this.camera.zoom);
    this.container.addChild(this.bossHealth);

    this.tutorialUI = new TutorialUI();
    this.stage.addChild(this.tutorialUI.container);

    this.ui = new DrawingGameUI(this);
    this.ui.on('home', () => {
      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(MainScene);

      logging(
        LOG_EVENT.GO_TO_HOME,
        LOG_SCENE.FINAL_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });
    this.ui.on('pause', () => {
      this.game.stop();
    });
    this.ui.on('start', () => {
      this.game.start();
    });

    this.ui.on('ad', async () => {
      try {
        const isLoaded: boolean = await loadGoogleAdvertisement();
        if (isLoaded) {
          await showGoogleAdvertisement();
          logging(LOG_EVENT.AD, LOG_SCENE.FINAL_GAME_SCENE, Store.instance.data.playername, window.navigator.userAgent);
        }
      } catch (e) {
        console.log(e);
      }
      if (Store.instance.state.hp < 3) {
        Store.instance.state.hp = 3;
      }

      const button = document.getElementById('sharebtn') as HTMLElement;
      button.style.display = 'none';
      this.game.changeScene(FinalGameScene);

      logging(LOG_EVENT.REPLAY, LOG_SCENE.FINAL_GAME_SCENE, Store.instance.data.playername, window.navigator.userAgent);
    });
    this.container.addChild(this.ui);

    this.on('shared', () => {
      this.ui.notificationNinimoDT = 120;

      logging(
        LOG_EVENT.COPY_SHARE,
        LOG_SCENE.FINAL_GAME_SCENE,
        Store.instance.data.playername,
        window.navigator.userAgent
      );
    });

    logging(
      LOG_EVENT.GAME_START,
      LOG_SCENE.FINAL_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
  }

  private gameOver(): void {
    logging(
      LOG_EVENT.GAME_OVER,
      LOG_SCENE.FINAL_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.canvas.pointerup();
    this.canvas.disable();
    this.ui.showTitle('Game Over');
    setTimeout(() => {
      this.ui.showGameOver(`4`, this.player.score.toString());
    }, 2000);
  }

  private gameClear(): void {
    logging(
      LOG_EVENT.GAME_CLEAR,
      LOG_SCENE.FINAL_GAME_SCENE,
      Store.instance.data.playername,
      window.navigator.userAgent
    );
    this.canvas.pointerup();
    this.canvas.disable();
    this.ui.showTitle('Clear!');
    this.player.trigger('CLEAR');

    this.isRunning = false;
    setTimeout(() => {
      const totalSeconds = Math.floor(this.duration / 1000);
      this.ui.showTitle(`${secondsFormatting(totalSeconds)}`);

      setTimeout(() => {
        sound.play('/audio/bonus.mp3', { loop: false, volume: Store.instance.state.volume as number });
        const score = secondsToScore(totalSeconds);
        this.player.score += score;
        this.ui.showTitle(`+${score} Score`);

        setTimeout(() => {
          if (!Store.instance.data.costumes[4]) {
            Store.instance.data.costumes[4] = true;
            Store.instance.save();
            this.ui.showGetItemPopup(4);
            this.ui.once('POPUP_END', () => {
              Store.instance.state.hp = this.player.hp;
              Store.instance.state.score = this.player.score;
              this.game.changeScene(ClosingScene);
            });
          } else {
            Store.instance.state.hp = this.player.hp;
            Store.instance.state.score = this.player.score;
            this.game.changeScene(ClosingScene);
          }
        }, 2500);
      }, 2500);
    }, 2500);
  }

  public update(elapsed: number): void {
    super.update(elapsed);

    this.particleEmitter.update(elapsed * 0.002);
    this.bossHealth.x = this.game.width / 2;
    this.bossHealth.y = this.game.height - 100;

    if (!this.tutorial.attack) {
      this.tutorialUI.update(0);
    } else {
      this.tutorialUI.container.visible = false;
    }

    if (this.lighting) {
      this.lighting.update();
      if (this.lighting.alpha === 0) {
        this.lighting.parent.removeChild(this.lighting);
        this.lighting.destroy();
        this.lighting = undefined;
      }
    }

    this.player.velocity.y += 1 * elapsed;
    if (this.player.position.y + this.player.velocity.y >= 1310) {
      this.player.velocity.y = 0;
      this.player.position.y = 1310;
      this.player.trigger(`LANDING`);
    }

    for (const object of this.objects) {
      object.update(elapsed);
    }

    this.canvas.update(elapsed);

    for (const cucumberProjectile of this.cucumberProjectiles) {
      const diff = Math.sqrt(
        (this.player.position.x - cucumberProjectile.position.x) ** 2 +
          (this.player.position.y - 70 - cucumberProjectile.position.y) ** 2
      );
      if (diff < 200 && cucumberProjectile.type === 1 && this.cucumber.attackProcess === 1) {
        this.player.fsm.transition('IDLE');
        this.player.update(1);
        this.tutorialUI.update(2);
        this.tutorialUI.container.visible = true;
        this.cucumber.animatedSprite.stop();
        return;
      } else if (diff < 150 && cucumberProjectile.type === 2 && this.cucumber.attackProcess === 3) {
        this.player.fsm.transition('IDLE');
        this.player.position.y = 1310;
        this.player.velocity.y = 0;
        this.player.update(1);
        this.tutorialUI.update(1);
        this.tutorialUI.container.visible = true;
        this.cucumber.animatedSprite.stop();
        return;
      } else {
        if (!this.cucumber.animatedSprite.playing) {
          this.cucumber.animatedSprite.play();
        }
        if (cucumberProjectile.x < 0) {
          this.stage.removeChild(cucumberProjectile);
          this.cucumberProjectiles.splice(this.cucumberProjectiles.indexOf(cucumberProjectile), 1);

          const score = 2;

          this.player.score += score;

          const scoreText: VanishText = new VanishText(`+${score}`, { fill: 0xefa500 });
          scoreText.x = this.player.position.x;
          scoreText.y = this.player.position.y - 90;
          scoreText.on('destroy', () => {
            this.objects.splice(this.objects.indexOf(scoreText), 1);
            this.stage.removeChild(scoreText);
            scoreText.destroy();
          });
          this.objects.push(scoreText);
          this.stage.addChild(scoreText);
        } else if (diff < 80 && this.player.invincibilityDT === 0) {
          if (cucumberProjectile.type >= 2 && this.player.state !== 'SLIDE') {
            sound.play('/audio/crash.mp3', { loop: false, volume: Store.instance.state.volume as number });
            sound.play('/audio/heartlose.mp3', { loop: false, volume: Store.instance.state.volume as number });
            this.player.hit(1);
            this.stage.removeChild(cucumberProjectile);
            this.cucumberProjectiles.splice(this.cucumberProjectiles.indexOf(cucumberProjectile), 1);
          } else if (cucumberProjectile.type === 1) {
            sound.play('/audio/crash.mp3', { loop: false, volume: Store.instance.state.volume as number });
            sound.play('/audio/heartlose.mp3', { loop: false, volume: Store.instance.state.volume as number });
            this.player.hit(1);
            this.stage.removeChild(cucumberProjectile);
            this.cucumberProjectiles.splice(this.cucumberProjectiles.indexOf(cucumberProjectile), 1);
          }
        }
      }

      cucumberProjectile.update(elapsed);
    }

    for (const playerProjectile of this.playerProjectiles) {
      playerProjectile.update(elapsed);

      if (playerProjectile.x > 720) {
        this.stage.removeChild(playerProjectile);
        this.playerProjectiles.splice(this.playerProjectiles.indexOf(playerProjectile), 1);

        if (!this.isRunning) {
          this.isRunning = true;
          this.isStarted = true;
        }

        sound.play('/audio/nini_attack.mp3', { loop: false, volume: Store.instance.state.volume as number });

        if (this.cucumber.health > 0) {
          this.cucumber.hit(1);
        }
        const score = 20;

        this.player.score += score;

        const scoreText: VanishText = new VanishText(`+${score}`, { fill: 0xefa500 });
        scoreText.x = this.player.position.x;
        scoreText.y = this.player.position.y - 90;
        scoreText.on('destroy', () => {
          this.objects.splice(this.objects.indexOf(scoreText), 1);
          this.stage.removeChild(scoreText);
          scoreText.destroy();
        });
        this.objects.push(scoreText);
        this.stage.addChild(scoreText);
      }
    }

    this.player.update(elapsed);

    if (this.player.hp > 0) {
      this.cucumber.update(elapsed);
    }

    this.ui.setHP(this.player.hp);
    this.ui.setScore(this.player.score);
    this.ui.update(elapsed);
    this.bossHealth.update(this.cucumber.health, this.cucumber.maxHealth);
  }

  public destroy(): void {
    super.destroy();
  }

  public static preload = async (callback: (currentValue: number, maxValue: number) => void): Promise<void> => {
    const assets: Array<string> = [
      '/audio/bgm_stage_4.mp3',
      '/audio/boss_attack.mp3',
      '/audio/boss_motion.mp3',
      '/audio/nini_attack.mp3',
      '/audio/nini_finalattack.mp3',
      '/audio/crash.mp3',
      '/audio/heartlose.mp3',
      '/audio/robot_destroy.mp3',

      '/textures/objects/monster/final_cucumber_1.webp',
      '/textures/objects/monster/final_cucumber_2.webp',
      '/textures/objects/monster/final_cucumber_3.webp',
      '/textures/objects/monster/final_cucumber_4.webp',
      '/textures/fx/fx2.webp',
      '/textures/fx/fx.webp',
      '/textures/bg/bg_final.png',
      '/textures/objects/ninimo/jump.webp',
      ...([0, 2, 3, 6].includes(Store.instance.state.costume) ? ['/textures/bg/bg_final_attack.png'] : []),
      ...([2].includes(Store.instance.state.costume) ? ['/textures/fx/meteor.webp', '/textures/fx/thunder.webp'] : []),
      `/textures/objects/ninimo/final_${Store.instance.state.costume}.webp`,
      `/textures/objects/ninimo/final_attack_${Store.instance.state.costume}.webp`,
      `/textures/objects/projectile/projectile_${Store.instance.state.costume}.png`,
      '/textures/objects/etc/projectile.png',
      '/textures/ui/health_0.png',
      '/textures/ui/health_1.png',
      '/textures/ui/health_2.png',
      '/textures/ui/health_bar.png',
      '/textures/ui/oee_0.png',
      '/textures/ui/oee_1.png',
      '/textures/ui/oee_2.png',
      '/textures/ui/oee_3.png',
    ];

    let maxLoaded = 1;
    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
        maxLoaded++;
      }
    }

    let loaded: number = 1;
    return new Promise((resolve) => {
      Loader.shared.onLoad.add(() => {
        callback(++loaded, maxLoaded);
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
    });
  };

  protected pointerdown = (event: InteractionEvent) => {
    if (this.game.paused) return;
    this.canvas.pointerdown(event);
  };

  protected pointermove = (event: InteractionEvent) => {
    if (this.game.paused) return;
    this.canvas.pointermove(event);
  };

  protected pointerup = (_event: InteractionEvent) => {
    if (this.game.paused) return;
    this.canvas.pointerup();
  };

  public onBegin(): void {
    if (!this.isBgmPlaying) {
      sound.play('/audio/bgm_stage_4.mp3', { loop: true, volume: Store.instance.state.volume as number });
      this.isBgmPlaying = true;
    }
  }
}

export default FinalGameScene;
